import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { toast } from "react-toastify";
import { BaseURL } from "../../../service/Api";
import { useThemeColors } from "../../../hooks/useThemeColors";
import { humanize } from "../roles/permissionUtils";
import { EXTERNAL_INDEX_PATH, ExternalResource } from "./types";
import TryItPanel, { TryItParams } from "./TryItPanel";

interface ApiResourceDocsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The table to document. When null the dialog documents the index + auth only. */
  resource: ExternalResource | null;
  tokenPrefix?: string;
  /** Full catalog; enables the "Try it" console when provided. */
  resources?: ExternalResource[];
  /** Allowed hosts of the application being edited, offered as origins in "Try it". */
  hosts?: string[];
  /** Open with the "Try it" console expanded. */
  startWithTryIt?: boolean;
}

type Lang = "curl" | "fetch" | "python";

const TOKEN_PLACEHOLDER = "mmsapp_YOUR_TOKEN";

const toQuery = (params: Record<string, string>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== "");
  return entries.length ? `?${entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")}` : "";
};

const ERROR_ROWS: [string, string][] = [
  ["401", "Missing, malformed, revoked or unknown token."],
  ["403", "Origin/Host not in the application's allowed hosts, or no grant for this table and verb."],
  ["404", "No row with that id."],
  ["405", "Write verbs (POST, PUT, PATCH, DELETE) are not enabled on the external API yet."],
  ["429", "Rate limit exceeded (600 requests per minute per application)."],
];

const buildSnippet = (
  lang: Lang,
  url: string,
  token: string,
  origin: string,
  params: Record<string, string>,
) => {
  const query = toQuery(params);
  const originHeader = origin || "https://your-allowed-host.example";
  if (lang === "curl") {
    return `curl "${url}${query}" \\\n  -H "Authorization: Bearer ${token}" \\\n  -H "Origin: ${originHeader}"`;
  }
  if (lang === "fetch") {
    return [
      `const response = await fetch("${url}${query}", {`,
      `  headers: { Authorization: "Bearer ${token}" },`,
      `});`,
      `const { count, next, previous, results } = await response.json();`,
    ].join("\n");
  }
  return [
    `import requests`,
    ``,
    `response = requests.get(`,
    `    "${url}",`,
    `    params=${JSON.stringify(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "")))},`,
    `    headers={"Authorization": "Bearer ${token}", "Origin": "${originHeader}"},`,
    `)`,
    `response.raise_for_status()`,
    `for row in response.json()["results"]:`,
    `    print(row)`,
  ].join("\n");
};

const ApiResourceDocsDialog: React.FC<ApiResourceDocsDialogProps> = ({
  open,
  onClose,
  resource,
  tokenPrefix,
  resources = [],
  hosts = [],
  startWithTryIt = false,
}) => {
  const colors = useThemeColors();
  const [lang, setLang] = useState<Lang>("curl");
  const [tryOpen, setTryOpen] = useState(startWithTryIt);
  const [tryState, setTryState] = useState<TryItParams | null>(null);
  const headingColor = colors.primary[100];
  const token = tokenPrefix ? `${tokenPrefix}…` : TOKEN_PLACEHOLDER;
  const base = BaseURL.replace(/\/$/, "");
  const url = resource ? `${base}${resource.path}` : `${base}${EXTERNAL_INDEX_PATH}`;
  const snippetParams = useMemo<Record<string, string>>(() => {
    if (tryState && tryState.slug === (resource?.slug ?? "index")) return tryState.params;
    return resource ? { page: "1", page_size: "20" } : {};
  }, [tryState, resource]);
  const snippet = useMemo(
    () => buildSnippet(lang, url, token, tryState?.origin ?? hosts[0] ?? "", snippetParams),
    [lang, url, token, tryState, hosts, snippetParams],
  );
  const handleTryChange = useCallback((state: TryItParams) => setTryState(state), []);

  React.useEffect(() => {
    if (open) setTryOpen(startWithTryIt);
  }, [open, startWithTryIt]);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard", { position: "top-right" });
  };

  const mono = { fontFamily: "monospace", fontSize: "0.8125rem" } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: headingColor, display: "flex", alignItems: "center", gap: 1 }}>
        <Box flexGrow={1} minWidth={0}>
          {resource ? `${humanize(resource.model)} API` : "External API"}
          {resource && (
            <Typography variant="body2" color="text.secondary">
              {resource.description}
            </Typography>
          )}
        </Box>
        {resources.length > 0 && (
          <Button
            size="small"
            variant={tryOpen ? "contained" : "outlined"}
            startIcon={<PlayArrowOutlinedIcon />}
            onClick={() => setTryOpen((v) => !v)}
          >
            Try it
          </Button>
        )}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {resources.length > 0 && (
            <Collapse in={tryOpen} unmountOnExit>
              <TryItPanel
                resources={resources}
                initialResource={resource}
                hosts={hosts}
                onChange={handleTryChange}
              />
            </Collapse>
          )}
          <Box>
            <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
              Endpoint
            </Typography>
            <Stack spacing={0.5}>
              {[
                ["GET", url, resource ? "List (paginated)" : "Index of tables this token may read"],
                ...(resource ? [["GET", `${url}{id}/`, "Retrieve one row"], ["GET", `${url}schema/`, "Field list"]] : []),
              ].map(([method, path, label]) => (
                <Stack key={path} direction="row" spacing={1} alignItems="center">
                  <Chip label={method} size="small" color="primary" />
                  <Typography sx={{ ...mono, wordBreak: "break-all", flexGrow: 1 }}>{path}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {label}
                  </Typography>
                  <IconButton size="small" onClick={() => copy(path)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
              Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Send the application token as a bearer token. Requests must originate from one of the
              application&apos;s allowed hosts (checked against the Origin, Referer or Host header) and
              the application needs a View grant on this table.
            </Typography>
            <Typography sx={mono}>Authorization: Bearer {token}</Typography>
          </Box>

          <Box>
            <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
              Query parameters
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={mono}>page</TableCell>
                  <TableCell>Page number, starting at 1.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={mono}>page_size</TableCell>
                  <TableCell>Rows per page. Default 10, maximum 1000.</TableCell>
                </TableRow>
                {resource && resource.search_fields.length > 0 && (
                  <TableRow>
                    <TableCell sx={mono}>search</TableCell>
                    <TableCell>Case-insensitive match on {resource.search_fields.join(", ")}.</TableCell>
                  </TableRow>
                )}
                {resource && resource.ordering_fields.length > 0 && (
                  <TableRow>
                    <TableCell sx={mono}>ordering</TableCell>
                    <TableCell>
                      One of {resource.ordering_fields.join(", ")}. Prefix with - for descending. Default{" "}
                      {resource.default_ordering.join(", ")}.
                    </TableCell>
                  </TableRow>
                )}
                {resource?.filterset_fields.map((f) => (
                  <TableRow key={f}>
                    <TableCell sx={mono}>{f}</TableCell>
                    <TableCell>Exact-match filter.</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {resource && (
            <Box>
              <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
                Response fields
              </Typography>
              <Box sx={{ maxHeight: 260, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resource.fields.map((f) => (
                      <TableRow key={f.name}>
                        <TableCell sx={mono}>{f.name}</TableCell>
                        <TableCell>{f.type}</TableCell>
                        <TableCell>{f.nullable ? "yes" : "no"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography fontWeight={700} sx={{ color: headingColor }}>
                Example
              </Typography>
              <Tabs value={lang} onChange={(_, v: Lang) => setLang(v)} sx={{ minHeight: 32 }}>
                <Tab value="curl" label="curl" sx={{ minHeight: 32, py: 0 }} />
                <Tab value="fetch" label="fetch" sx={{ minHeight: 32, py: 0 }} />
                <Tab value="python" label="Python" sx={{ minHeight: 32, py: 0 }} />
              </Tabs>
            </Stack>
            <Paper
              sx={{
                boxShadow: 0,
                bgcolor: colors.primary[800],
                color: colors.primary[100],
                p: 2,
                borderRadius: 2,
                position: "relative",
                overflow: "auto",
              }}
            >
              <IconButton size="small" onClick={() => copy(snippet)} sx={{ position: "absolute", top: 4, right: 4 }}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
              <pre style={{ margin: 0, ...mono, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{snippet}</pre>
            </Paper>
          </Box>

          <Divider />
          <Box>
            <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
              Errors
            </Typography>
            <Table size="small">
              <TableBody>
                {ERROR_ROWS.map(([code, text]) => (
                  <TableRow key={code}>
                    <TableCell sx={{ ...mono, width: 64 }}>{code}</TableCell>
                    <TableCell>{text}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiResourceDocsDialog;
